import { CSSProperties, useMemo, useState } from 'react';
import { useFormState } from 'informed';
import { useReduxState } from 'hooks';
import { localeLanguageCodes, localeNames } from 'shared-locales';
import { traverseNestedObjectAndGrabValue } from 'shared-helpers/common';
import { Expand, FlagIcon } from 'components/common';
import { Icon } from 'components/common/icons';
import {
  ImageUploader,
  NumberStepper,
  RichText,
  RichTextOrHtml,
  Text,
  TextArea
} from '.';

import type { Locales } from 'shared-types';

type ImageUploaderProps = React.ComponentProps<typeof ImageUploader>;
type NumberStepperProps = React.ComponentProps<typeof NumberStepper>;
type RichTextProps = React.ComponentProps<typeof RichText>;
type RichTextOrHtmlProps = React.ComponentProps<typeof RichTextOrHtml>;
type TextProps = React.ComponentProps<typeof Text>;
type TextAreaProps = React.ComponentProps<typeof TextArea>;

type CustomLocale = { key: string; label: string; icon: string };

type Props = {
  customLocales?: CustomLocale[];
  field: string;
  style?: CSSProperties;
  supportedLocales?: string[];
} & (
  | {
      customProps: {
        localeWidth?: Record<Locales, number>;
        localeUploaders: React.RefObject<any>;
      } & ImageUploaderProps;
      type: 'image';
    }
  | { customProps?: NumberStepperProps; type: 'number' }
  | { customProps?: Partial<RichTextProps>; type: 'richtext' }
  | { customProps?: RichTextOrHtmlProps; type: 'richtextorhtml' }
  | { customProps?: TextProps; type: 'text' }
  | { customProps?: TextAreaProps; type: 'textarea' }
);

const LanguageVariants = ({
  customLocales,
  customProps,
  field,
  style,
  supportedLocales,
  type
}: Props) => {
  const { messages, theme } = useReduxState();

  const formState = useFormState();

  const [isExpanded, setIsExpanded] = useState(false);

  const Element = {
    image: ImageUploader,
    number: NumberStepper,
    richtext: RichText,
    richtextorhtml: RichTextOrHtml,
    text: Text,
    textarea: TextArea
  }[type];

  const elementProps = {
    image: {},
    number: {},
    richtext: { rows: 3 },
    richtextorhtml: { rows: 3 },
    text: {},
    textarea: { autoGrow: true, rows: 3 }
  }[type];

  const { locales, localeLabels, localeIconsMap } = useMemo(() => {
    let locales: string[] = [];
    let localeLabels: Record<string, string> = {};
    let localeIconsMap: Record<string, string> = {};

    if (customLocales && customLocales.length > 0) {
      customLocales.forEach((l) => {
        locales.push(l.key);
        localeLabels[l.key] = l.label;
        localeIconsMap[l.key] = l.icon;
      });
    } else {
      locales = Object.keys(localeLanguageCodes);
      localeLabels = localeNames;
      localeIconsMap = localeLanguageCodes;
    }
    return { locales, localeLabels, localeIconsMap };
  }, [customLocales]);

  const translatedLocales = locales.filter((locale) => {
    const value = traverseNestedObjectAndGrabValue(formState.values, field);
    return value?.[locale];
  });

  return (
    <div className="variants" style={style}>
      <div className="controller" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="indicator">
          <div className="title">
            {messages.settingsLanguageVariants}

            <div className="icon">
              <Icon icon={isExpanded ? 'chevron-up' : 'chevron-down'} />
            </div>
          </div>

          <div className="indicator-locales">
            {translatedLocales.map((locale) => (
              <div
                className="indicator-flag"
                key={locale}
                aria-label={localeLabels[locale]}
                data-tooltip={localeLabels[locale]}
              >
                <FlagIcon
                  lang={localeIconsMap[locale]}
                  style={{ marginTop: -1 }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <Expand isExpanded={isExpanded}>
        <div className="fields">
          {locales
            .filter(
              (locale) =>
                !supportedLocales?.length || supportedLocales.includes(locale)
            )
            .map((locale) => (
              <div className="field" key={`${field}-${locale}`}>
                <div
                  className="flag"
                  aria-label={localeNames[locale]}
                  data-tooltip={localeNames[locale]}
                >
                  <FlagIcon
                    lang={localeLanguageCodes[locale]}
                    style={{ marginTop: -1 }}
                  />
                </div>

                <div style={{ width: '100%' }}>
                  <Element
                    field={`${field}.${locale}`}
                    id={`${field}-${locale}`}
                    placeholder={messages.settingsLanguageVariantsPlaceholder}
                    {...elementProps}
                    {...customProps}
                    {...(type === 'image'
                      ? {
                          maxWidth:
                            customProps.localeWidth?.[locale] ||
                            customProps.maxWidth,
                          setUploader: (uploader) =>
                            (customProps.localeUploaders[locale] = uploader)
                        }
                      : {})}
                    {...(type === 'richtext' &&
                    customProps?.templateTagsIdentifier
                      ? {
                          templateTagsIdentifier: `${customProps.templateTagsIdentifier}-${locale}`
                        }
                      : {})}
                  />
                </div>
              </div>
            ))}
        </div>
      </Expand>

      <style jsx>{`
        .variants {
          margin-top: 5px;
        }
        .controller {
          align-items: center;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          padding: 3px 3px;
          width: 100%;
        }
        .indicator {
          align-items: center;
          color: ${theme.textColorLight};
          display: flex;
          font-size: 1.4rem;
          font-weight: 600;
          gap: 15px;
        }
        .title {
          align-items: center;
          display: flex;
          gap: 5px;
        }
        .icon {
          color: ${theme.textColorLight};
          font-size: 1.2rem;
          position: relative;
          top: 1px;
        }
        .indicator-locales {
          display: flex;
          gap: 10px;
        }
        .count {
          background-color: ${theme.lightGreyDark};
          border-radius: 100%;
          font-size: 1.2rem;
          font-weight: 700;
          padding: 2px 8px;
        }
        .fields {
          margin-top: 5px;
        }
        .field {
          align-items: ${type === 'text' ? 'center' : 'flex-start'};
          display: flex;
          gap: 10px;
          padding: 3px;
          width: 100%;
        }
        .field:not(:first-child) {
          margin-top: 5px;
        }
      `}</style>
    </div>
  );
};

export default LanguageVariants;
